import React, { useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Mic, FileText, Database, Shield, BookOpen, TrendingUp, Upload, GraduationCap, Sparkles } from 'lucide-react';
import Footer from '../components/Footer';
import PageTitle from '../components/PageTitle';
import { useTranslation } from 'react-i18next';

const Features = () => {
  const { t } = useTranslation();
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
  
  const features = [
    {
      icon: Mic,
      title: t('features.voiceSimulation'),
      description: t('features.voiceSimulationDesc')
    },
    {
      icon: FileText,
      title: t('features.toneStyle'),
      description: t('features.toneStyleDesc')
    },
    {
      icon: Database,
      title: t('features.memoryAnchoring'),
      description: t('features.memoryAnchoringDesc')
    },
    {
      icon: Shield,
      title: t('features.valueBasedResponses'),
      description: t('features.valueBasedResponsesDesc')
    },
    {
      icon: BookOpen,
      title: t('features.lifeStory'),
      description: t('features.lifeStoryDesc')
    },
    {
      icon: TrendingUp,
      title: t('features.identityContinuity'),
      description: t('features.identityContinuityDesc')
    },
    {
      icon: Upload,
      title: t('features.socialMediaImport'),
      description: t('features.socialMediaImportDesc')
    },
    {
      icon: GraduationCap,
      title: t('features.trainingModule'),
      description: t('features.trainingModuleDesc')
    },
    {
      icon: Sparkles,
      title: t('features.autoLearning'),
      description: t('features.autoLearningDesc')
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen">
      <PageTitle title="Features - Advanced Digital Presence Tools" />
      {/* BLOCK 1 - HERO */}
      <section className="pt-32 pb-24 lg:pt-40 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h1 className="text-6xl lg:text-8xl font-bold mb-8 reveal-on-scroll animate-letter-spacing">
            {t('features.title')}
          </h1>
          <p className="text-2xl lg:text-3xl text-cyan-400 font-light max-w-4xl mx-auto reveal-on-scroll shimmer">
            {t('features.subtitle')}
          </p>
        </div>
      </section>

      {/* BLOCK 2 - FEATURE GRID */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-black via-[#0B1F2E] to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <Card 
                key={idx} 
                className="bg-gradient-to-br from-gray-900/80 to-gray-800/60 border-cyan-500/20 hover:border-cyan-500/60 transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/20 group reveal-on-scroll lift-on-hover neon-border-hover"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <CardContent className="p-10 space-y-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 tilt-on-hover">
                    <feature.icon className="text-cyan-400 energy-pulse" size={32} />
                  </div>
                  <div className="h-1 w-16 bg-gradient-to-r from-cyan-500 to-transparent liquid-flow" />
                  <h3 className="text-2xl font-bold">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed text-lg">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Features;
